import React from 'react';
import { faEllipsisVertical, faListCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHistory } from 'react-router-dom';
import { WidgetContainer } from '@frontend/app/refresh-components';
import { cn } from '@frontend/shadcn/lib/utils';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import Empty from '@frontend/app/refresh-components/Empty';
import { useGetTasksForProgramQuery } from '@frontend/app/hooks';
import { SHOPIFY_APP_ID, SOCIAL_DISCOVERY_APP_ID, SOCIAL_POST_APP_ID } from '@frontend/app/constants/applicationIds';
import { TaskType } from '@common';
import { TaskCard } from './TaskCard';
import { TASK_CONFIG } from './taskConfig';

interface CurrentTasksProps {
  projectId: number;
  className?: string;
}

// Skeleton component for loading state
const TaskCardSkeleton: React.FC = () => (
  <div className="w-full p-4 bg-white rounded-2xl border border-gray-200">
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Skeleton className="w-5 h-5 rounded" />
        <Skeleton className="h-4 w-3/4" />
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-2/3" />
    </div>
  </div>
);

// Loading skeleton for the entire widget
const TasksLoadingSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full">
    {Array.from({ length: 4 }).map((_, index) => (
      <TaskCardSkeleton key={index} />
    ))}
  </div>
);

export const CurrentTasks = ({ projectId, className }: CurrentTasksProps) => {
  const history = useHistory();
  const { data, loading, error } = useGetTasksForProgramQuery({
    variables: { programId: projectId },
    skip: !projectId,
    fetchPolicy: 'network-only',
  });

  const tasks = data?.getTasksForProgram?.tasks || [];
  const pendingTasks = tasks.filter((task) => !task.isCompleted);

  const handleTaskClick = (taskName: string) => {
    const config = TASK_CONFIG[taskName];
    if (!config) return;
    const metadata = pendingTasks.find((task) => task.taskName === taskName)?.metadata;
    // Map task URLs to actual application routes
    let navigationUrl = '';

    switch (taskName) {
      // Workflow tasks - all use the same navigation pattern
      case TaskType.ReviewBriefs:
      case TaskType.ReviewContents:
      case TaskType.ReviewProductSubmissions:
      case TaskType.SendBriefs:
      case TaskType.SendWelcomeEmails:
      case TaskType.SendProductCatalog:
      case TaskType.SendContracts:
      case TaskType.SendPromoCodes:
      case TaskType.SendSalesLinks:
      case TaskType.SendPayments:
        navigationUrl = `/projects/${projectId}/${metadata?.workletSpecUri}/${metadata?.taskId}`;
        break;

      // Collaboration tasks
      case TaskType.CollaborateCreators:
        navigationUrl = `/app/${SOCIAL_DISCOVERY_APP_ID}`;
        break;
      case TaskType.ReviewApplicants:
        navigationUrl = `/projects/${projectId}/applicants`;
        break;

      // Integration tasks
      case TaskType.ConnectShopify:
        navigationUrl = `/settings/${SHOPIFY_APP_ID}`;
        break;
      case TaskType.ConnectInstagram:
        navigationUrl = `/settings/${SOCIAL_POST_APP_ID}`;
        break;
      case TaskType.ConnectTikTok:
        navigationUrl = '/settings/tiktok';
        break;

      // Project Settings tasks
      case TaskType.CreateCampaignWorkflow:
        navigationUrl = `/projects/${projectId}/settings/workflow`;
        break;
      case TaskType.DesignApplicationPage:
        navigationUrl = `/projects/${projectId}/settings/recruitment`;
        break;
      case TaskType.ConfigureBriefTemplate:
        navigationUrl = `/projects/${projectId}/settings/terms`;
        break;
      case TaskType.SetupProductCatalog:
        navigationUrl = `/projects/${projectId}/settings/product_catalog`;
        break;
      case TaskType.DefineOffers:
        navigationUrl = `/projects/${projectId}/settings/offers`;
        break;
      case TaskType.ReviewAutomations:
        navigationUrl = `/projects/${projectId}/settings/automation`;
        break;
      case TaskType.ReviewBudget:
        navigationUrl = `/projects/${projectId}/settings/budget`;
        break;

      default:
        break;
    }

    // Navigate to the determined URL
    if (navigationUrl) {
      // Pass the current location as state for back navigation
      const currentPath = history.location.pathname;
      history.push({
        pathname: navigationUrl,
        state: { from: currentPath }
      });
    }
  };

  // Render loading state
  if (loading) {
    return (
      <WidgetContainer
        widgetTitle="Current Tasks"
        textColor="text-grey-6 text-sm"
        bgColor="bg-white"
        widgetIcon={<FontAwesomeIcon icon={faListCheck} className="text-grey-4" />}
        widgetActions={<FontAwesomeIcon icon={faEllipsisVertical} className="text-grey-6" />}
        className={cn('h-min', className)}
      >
        <TasksLoadingSkeleton />
      </WidgetContainer>
    );
  }

  // Render error state
  if (error) {
    return (
      <WidgetContainer
        widgetTitle="Current Tasks"
        textColor="text-grey-6 text-sm"
        bgColor="bg-white"
        widgetIcon={<FontAwesomeIcon icon={faListCheck} className="text-grey-4" />}
        widgetActions={<FontAwesomeIcon icon={faEllipsisVertical} className="text-grey-6" />}
        className={cn('h-min', className)}
      >
        <div className="py-8">
          <Empty
            title="Failed to load tasks"
            description="There was an error loading your current tasks. Please try refreshing the page."
            size="md"
            type="error"
          />
        </div>
      </WidgetContainer>
    );
  }

  // Render empty state
  if (pendingTasks.length === 0) {
    const emptyMessage =
      tasks.length === 0
        ? {
            title: 'No tasks found',
            description: 'There are no tasks available for this program yet.',
          }
        : {
            title: 'All tasks completed!',
            description: "Great job! You've completed all the current tasks for this program.",
          };

    return (
      <WidgetContainer
        widgetTitle="Current Tasks"
        textColor="text-grey-6 text-sm"
        bgColor="bg-white"
        widgetIcon={<FontAwesomeIcon icon={faListCheck} className="text-grey-4" />}
        widgetActions={<FontAwesomeIcon icon={faEllipsisVertical} className="text-grey-6" />}
        className={cn('h-min', className)}
      >
        <div className="py-8">
          <Empty title={emptyMessage.title} description={emptyMessage.description} size="md" />
        </div>
      </WidgetContainer>
    );
  }

  // Render tasks
  return (
    <WidgetContainer
      widgetTitle="Current Tasks"
      textColor="text-grey-6 text-sm"
      bgColor="bg-white"
      widgetIcon={<FontAwesomeIcon icon={faListCheck} className="text-grey-4" />}
      widgetActions={<FontAwesomeIcon icon={faEllipsisVertical} className="text-grey-6" />}
      className={cn('h-min', className)}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full">
        {pendingTasks.map((task) => {
          const config = TASK_CONFIG[task.taskName];

          if (!config) {
            return null;
          }

          // Format title with member count if available
          const memberCount = task.metadata?.memberCount;
          const titleWithCount = memberCount !== undefined && memberCount > 0
            ? `${config.title} (${memberCount})`
            : config.title;

          return (
            <TaskCard
              key={task.taskName}
              icon={<FontAwesomeIcon icon={config.icon} className="text-xs" />}
              title={titleWithCount}
              description={config.description}
              onClick={() => handleTaskClick(task.taskName)}
            />
          );
        })}
      </div>
    </WidgetContainer>
  );
};
